import {AppBar, Box, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Toolbar}
    from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import {Marshal, MarshalData} from "../../fstn/marshal";
import Dialog from "../dialogs/Dialog";
import {connect} from "react-redux";
import React, {useCallback, useContext, useState} from "react";
import Actions from "./Actions";
import {ActionFactory} from "../../fstn/actionFactory";
import {Add} from "@mui/icons-material";
import {MarshalValueData, RegisterReference} from "../../fstn/marshalValue";
import {Transition} from "../../fstn/transition";
import {RegisterValue} from "../../fstn/register";
import {Network} from "../../fstn/network";
import {selectConstants, selectLocks, selectProjects} from "../../redux/selectors/firebase";
import {ProjectDetails} from "../../redux/types/firebase";
import {IDebugController} from "../../fstn/debugController";
import { createLink } from "../../utility/url";
import {selectMode, selectUserId} from "../../redux/selectors/editor";
import { StateTools } from "../writer/StateTools";
import { updateMode, updateFocus } from "../../redux/slices/editor";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {Locks} from "../navbar/StateLock";
import StateTags from "./StateTags";

const useStyles = makeStyles(theme => ({
    actions: {
        maxHeight: "80ch",
        overflowY: "scroll",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    icon: {
        color: theme.palette.common.white
    },
    select: {
        color: theme.palette.common.white
    }
}));

interface Props {
    id: string
    data: {
        actions?: Marshal[],
        script: string,
        debugController: IDebugController | undefined
        network: Network,
        parent: string
        label: string
        transitions: Array<Transition>,
    }
    mode: string
    factory: ActionFactory
    locks: Locks
    project: string
    projects: {[key: string]: ProjectDetails}
    onClose: () => any
    onUpdateMember: (id: string, member: string, value: any) => void
    updateMode: (mode: string) => void
    updateFocus: (focus: string) => void
    userKey: string
}
function StateDetails({id, data, mode, factory, locks, project, projects,  onClose, onUpdateMember,
                          updateMode, updateFocus, userKey}: Props) {
    const classes = useStyles();
    const [addAction, setAddAction] = useState<string>("");
    const [name, setName] = useState<string>(data.label);
    const {setStateMember} = useContext(FirebaseContext);
    const handleChangeName = ({target}: React.ChangeEvent<HTMLInputElement>) => setName(target.value);
    const handleChangeActionReference = (marshal: number, parameter: string, reference: RegisterReference | undefined) => {
        const actions = (data.actions || []).map(action => action.serialise());

        if (reference === undefined) {
            delete actions[marshal].parameters[parameter];
        } else {
            actions[marshal].parameters[parameter] = {reference};
        }
        setStateMember(project, id, "actions", actions);
    };
    const handleChangeActionValue = (marshal: number, parameter: string, value: RegisterValue | undefined) => {
        const actions = (data.actions || []).map(action => action.serialise());

        if (value === undefined) {
            delete actions[marshal].parameters[parameter];
        } else {
            if (typeof value === "number") {
                actions[marshal].parameters[parameter] = {value: value as number};
            } else if (typeof value === "boolean") {
                actions[marshal].parameters[parameter] = {value: value as boolean};
            } else {
                actions[marshal].parameters[parameter] = {value: value as string};
            }
        }
        setStateMember(project, id, "actions", actions);
    }
    const handleNameBlur = () => {
        if (name !== data.label) {
            onUpdateMember(id, "name", name);
        }
    }
    const handleUpdateActions = (actions: MarshalData[]) => {
        onUpdateMember(id, "actions", actions);
    }
    const handleAddActionChanged = ({target}: SelectChangeEvent) => {
        setAddAction(target.value as string);
    };
    const handleClickAddAction = () => {
        const extendedActions = (data.actions || []).map(action => action.serialise());
        const action = factory.get(addAction);
        const parameterValues: {[key: string]: () => RegisterValue} = {
            trigger: () => (data.transitions.length && data.transitions[0].trigger) || "continue"
        }

        if (action) {
            const parameters: { [key: string]: MarshalValueData } = {};

            Object.keys(action.details.parameters).forEach(key => {
                const description = action.details.parameters[key];
                let value = description.defaultValue;

                if (value === undefined && description.type !== undefined) {
                    // Check type is not array of values
                    if (typeof description.type === "string") {
                        if (parameterValues[description.type]) {
                            value = parameterValues[description.type]();
                        }
                    }
                }
                if (value !== undefined) {
                    parameters[key] = {value}
                }
            });
            extendedActions.push({action:addAction, parameters})
            handleUpdateActions(extendedActions);
        }
    };
    const handleChangeParent = ({target}: SelectChangeEvent) => {
        setStateMember(project, id, "parent", target.value);
    }
    const isLocked = useCallback((stateKey: string, network: Network, locks: Locks, userKey: string): boolean =>
    {
        if (locks && stateKey)
        {
            if (locks[stateKey])
            {
                return locks[stateKey].user !== userKey;
            } else
            {
                const state = network.states.get(stateKey);

                if (state)
                {
                    return isLocked(state.parent, network, locks, userKey);
                }
            }
        }
        return false;
    }, []);
    const locked = isLocked(id, data.network, locks, userKey);
    const selectClasses = {
        icon: classes.icon,
        select: classes.select
    };
    const renderSelected = (selected: any) => selected === "" ? <em>Add Action</em> : selected as string;
    const url = createLink({state: id, mode, project});
    const gotoAction = data.script.length > 0 ? () => {
        updateMode("writer"); 
        updateFocus(id);
    }: undefined

    return (
        <Dialog onClose={onClose}>
            <AppBar position="static">
                <Toolbar variant="dense">
                    <StateTools state={id} iconClassName={classes.icon} linkUrl={url} gotoAction={gotoAction}/>
                    <Box flexGrow={1}>&nbsp;</Box>
                    <FormControl variant="standard" style={{minWidth: 120}}>
                        <Select classes={selectClasses} disabled={locked} value={addAction} displayEmpty
                                onChange={handleAddActionChanged} renderValue={renderSelected} size="small">
                            <MenuItem value="" disabled>
                                <em>Add action</em>
                            </MenuItem>
                            {factory.getActions()
                                .sort((lhs, rhs) => lhs.localeCompare(rhs))
                                .map(action => (
                                    <MenuItem key={action} value={action}>{action}</MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <IconButton color="inherit" edge="end" onClick={handleClickAddAction} disabled={addAction === ""}
                        size="large">
                        <Add/>
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Box p={2} className={classes.actions}>
                <Box m={1}>
                    <TextField
                        fullWidth label="name" onChange={handleChangeName} onBlur={handleNameBlur} size="small"
                        value={name} variant="outlined"/>
                </Box>
                <Box m={1}>
                    <FormControl fullWidth>
                        <InputLabel id="select-parent">Parent State</InputLabel>
                        <Select disabled={locked} label="Parent State"
                                labelId="select-parent" value={data.parent} onChange={handleChangeParent}>
                            <MenuItem value={""}><em>No Parent</em></MenuItem>
                            {Array.from(data.network.states.keys()).map(stateKey => (
                                <MenuItem key={stateKey} value={stateKey}>
                                    {`${stateKey} (${data.network.states.get(stateKey)?.name})`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <StateTags project={project} state={id}/>
                <Actions
                    actions={data.actions || []} factory={factory} project={project} transitions={data.transitions}
                    projectDetails={projects[project]} network={data.network}
                    onChangeActionReference={handleChangeActionReference}
                    onChangeActionValue={handleChangeActionValue} onUpdateActions={handleUpdateActions}/>
            </Box>
        </Dialog>
    );
}
const mapStateToProps = (store: never) => ({
    constants: selectConstants(store),
    locks: selectLocks(store),
    projects: selectProjects(store),
    mode: selectMode(store),
    userKey: selectUserId(store)
});

export default connect(mapStateToProps, {
    updateMode,
    updateFocus
})(StateDetails);
